import { Undo, Redo, ZoomOut, ZoomIn } from 'lucide-react'
import { useState, useRef, useEffect, useMemo } from 'react'
import { useEditor } from '../contexts/EditorContext'

interface VideoClip {
  id: string
  url: string
  startTime: number
  duration: number
}

interface DragState {
  type: 'clip' | 'trim-left' | 'trim-right' | 'playhead' | null
  clipId?: string
  startX: number
  initialValue: number
}

export default function TimelinePanel() {
  const { currentTime, duration, setCurrentTime } = useEditor()
  const timelineRef = useRef<HTMLDivElement>(null)
  const [dragState, setDragState] = useState<DragState>({ type: null, startX: 0, initialValue: 0 })
  const [hoveredClipId, setHoveredClipId] = useState<string | null>(null)

  // Pixels per second - controls timeline zoom
  const pixelsPerSecond = 20

  // Video clips data - in a real app, this would come from state management
  const [videoClips, setVideoClips] = useState<VideoClip[]>([
    {
      id: '1',
      url: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=100&h=64&fit=crop',
      startTime: 0,
      duration: 15,
    },
    {
      id: '2',
      url: 'https://images.unsplash.com/photo-1518717758536-85ae29035b6d?w=100&h=64&fit=crop',
      startTime: 15,
      duration: 15,
    },
    {
      id: '3',
      url: 'https://images.unsplash.com/photo-1426604966848-d7adac402bff?w=100&h=64&fit=crop',
      startTime: 30,
      duration: 15,
    },
    {
      id: '4',
      url: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=100&h=64&fit=crop',
      startTime: 45,
      duration: 15,
    },
    {
      id: '5',
      url: 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=100&h=64&fit=crop',
      startTime: 60,
      duration: 15,
    },
    {
      id: '6',
      url: 'https://images.unsplash.com/photo-1472214103451-9374bd1c798e?w=100&h=64&fit=crop',
      startTime: 75,
      duration: 17.5,
    },
  ])

  // Convert time to pixel position
  const timeToPixel = (time: number) => {
    return time * pixelsPerSecond
  }

  // Convert pixel position to time
  const pixelToTime = (pixel: number) => {
    return Math.max(0, Math.min(duration, pixel / pixelsPerSecond))
  }

  // Format time for display
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Generate time markers
  const generateTimeMarkers = () => {
    const markers = []
    const interval = 4 // 4 seconds per marker
    for (let i = 0; i <= Math.ceil(duration / interval); i++) {
      const time = i * interval
      markers.push(time)
    }
    return markers
  }

  // Handle playhead drag start
  const handlePlayheadMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setDragState({
      type: 'playhead',
      startX: e.clientX,
      initialValue: currentTime,
    })
  }

  // Handle clip drag start
  const handleClipMouseDown = (e: React.MouseEvent, clipId: string, clip: VideoClip) => {
    e.preventDefault()
    e.stopPropagation()
    setDragState({
      type: 'clip',
      clipId,
      startX: e.clientX,
      initialValue: clip.startTime,
    })
  }

  // Handle trim drag start
  const handleTrimMouseDown = (
    e: React.MouseEvent,
    clipId: string,
    side: 'left' | 'right',
    clip: VideoClip
  ) => {
    e.preventDefault()
    e.stopPropagation()
    setDragState({
      type: side === 'left' ? 'trim-left' : 'trim-right',
      clipId,
      startX: e.clientX,
      initialValue: side === 'left' ? clip.startTime : clip.duration,
    })
  }

  // Handle mouse move
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragState.type || !timelineRef.current) return

      const deltaX = e.clientX - dragState.startX
      const deltaTime = deltaX / pixelsPerSecond

      if (dragState.type === 'playhead') {
        const newTime = Math.max(0, Math.min(duration, dragState.initialValue + deltaTime))
        setCurrentTime(newTime)
      } else if (dragState.type === 'clip' && dragState.clipId) {
        const newStartTime = Math.max(0, Math.min(duration, dragState.initialValue + deltaTime))
        setVideoClips((clips) =>
          clips.map((clip) =>
            clip.id === dragState.clipId ? { ...clip, startTime: newStartTime } : clip
          )
        )
      } else if (dragState.type === 'trim-left' && dragState.clipId) {
        const clip = videoClips.find((c) => c.id === dragState.clipId)
        if (clip) {
          const newStartTime = Math.max(
            0,
            Math.min(clip.startTime + clip.duration - 1, dragState.initialValue + deltaTime)
          )
          const newDuration = clip.duration + (clip.startTime - newStartTime)
          setVideoClips((clips) =>
            clips.map((c) =>
              c.id === dragState.clipId
                ? { ...c, startTime: newStartTime, duration: newDuration }
                : c
            )
          )
        }
      } else if (dragState.type === 'trim-right' && dragState.clipId) {
        const newDuration = Math.max(1, dragState.initialValue + deltaTime)
        setVideoClips((clips) =>
          clips.map((clip) =>
            clip.id === dragState.clipId ? { ...clip, duration: newDuration } : clip
          )
        )
      }
    }

    const handleMouseUp = () => {
      setDragState({ type: null, startX: 0, initialValue: 0 })
    }

    if (dragState.type) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
    return undefined
  }, [dragState, duration, setCurrentTime, videoClips, pixelsPerSecond])

  // Handle timeline click
  const handleTimelineClick = (e: React.MouseEvent) => {
    if (!timelineRef.current || dragState.type) return
    const rect = timelineRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const newTime = pixelToTime(x)
    setCurrentTime(newTime)
  }

  const playheadPosition = timeToPixel(currentTime)
  const timeMarkers = generateTimeMarkers()

  // Generate fixed waveform heights (won't change on re-render)
  const waveformHeights = useMemo(() => {
    return Array.from({ length: 100 }, () => 30 + Math.random() * 70)
  }, [])

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Timeline Toolbar */}
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/50 px-4 py-2">
        <div className="flex gap-2">
          <button className="rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-900">
            <Undo className="h-4 w-4" />
          </button>
          <button className="rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-900">
            <Redo className="h-4 w-4" />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button className="rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-900">
            <ZoomOut className="h-4 w-4" />
          </button>
          <div className="h-1.5 w-32 overflow-hidden rounded-full bg-gray-200">
            <div className="h-full w-1/2 rounded-full bg-gray-400" />
          </div>
          <button className="rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-900">
            <ZoomIn className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Timeline Tracks */}
      <div className="custom-scrollbar relative flex-1 overflow-x-auto overflow-y-hidden p-4">
        <div
          ref={timelineRef}
          className="relative min-w-full"
          style={{ width: `${duration * pixelsPerSecond}px` }}
          onClick={handleTimelineClick}
        >
          {/* Time markers */}
          <div className="relative mb-2 flex h-8 text-xs font-medium text-gray-400">
            {timeMarkers.map((time, i) => (
              <div
                key={i}
                className="absolute top-0 flex flex-col items-start"
                style={{ left: `${timeToPixel(time)}px` }}
              >
                <span className="mb-1 text-gray-500">{formatTime(time)}</span>
                <div className="h-3 w-px bg-gray-300" />
              </div>
            ))}
          </div>

          {/* Playhead */}
          <div
            className="pointer-events-none absolute bottom-0 top-0 z-20"
            style={{ left: `${playheadPosition}px` }}
          >
            <div
              className="pointer-events-auto absolute -left-1.5 -top-1 h-3.5 w-3.5 cursor-grab rounded-full bg-blue-500 shadow-md active:cursor-grabbing"
              onMouseDown={handlePlayheadMouseDown}
            >
              <div className="absolute inset-0 rounded-full bg-white opacity-30" />
            </div>
            <div className="h-full w-0.5 bg-blue-500 shadow-sm" />
          </div>

          <div className="mt-4 space-y-3">
            {/* Video Track */}
            <div className="relative h-16">
              {videoClips.map((clip) => {
                const left = timeToPixel(clip.startTime)
                const width = timeToPixel(clip.duration)
                const isHovered = hoveredClipId === clip.id

                return (
                  <div
                    key={clip.id}
                    className="absolute h-full cursor-move overflow-hidden rounded-md border-2 border-white ring-1 ring-gray-200 transition-all hover:ring-blue-400"
                    style={{ left: `${left}px`, width: `${width}px` }}
                    onMouseDown={(e) => handleClipMouseDown(e, clip.id, clip)}
                    onMouseEnter={() => setHoveredClipId(clip.id)}
                    onMouseLeave={() => setHoveredClipId(null)}
                  >
                    <img
                      src={clip.url}
                      className="h-full w-full object-cover opacity-80"
                      alt={`Clip ${clip.id}`}
                    />

                    {/* Left trim handle */}
                    {isHovered && (
                      <div
                        className="absolute bottom-0 left-0 top-0 w-2 cursor-ew-resize bg-white/80 transition-colors hover:bg-blue-400"
                        onMouseDown={(e) => handleTrimMouseDown(e, clip.id, 'left', clip)}
                      >
                        <div className="absolute left-1/2 top-1/2 h-8 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded bg-gray-600" />
                      </div>
                    )}

                    {/* Right trim handle */}
                    {isHovered && (
                      <div
                        className="absolute bottom-0 right-0 top-0 w-2 cursor-ew-resize bg-white/80 transition-colors hover:bg-blue-400"
                        onMouseDown={(e) => handleTrimMouseDown(e, clip.id, 'right', clip)}
                      >
                        <div className="absolute left-1/2 top-1/2 h-8 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded bg-gray-600" />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Audio Track 1 - Speech */}
            <div className="flex h-8 items-center overflow-hidden rounded-lg border border-blue-100 bg-blue-50 px-1">
              {/* Fake waveform */}
              <div className="flex h-5 w-full items-end gap-0.5 px-1 opacity-60">
                {waveformHeights.map((height, i) => (
                  <div
                    key={i}
                    className="w-1 rounded-full bg-blue-400"
                    style={{ height: `${height}%` }}
                  />
                ))}
              </div>
            </div>

            {/* Audio Track 2 - Music */}
            <div className="flex h-8 items-center overflow-hidden rounded-lg border border-gray-700 bg-gray-800 px-3 shadow-sm">
              <span className="truncate text-[10px] font-medium tracking-wide text-white/90">
                Chinese Happy New Year - Festive Background Music
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
